import { useToast } from "@mallardbay/lib-react-components"

import {
    useAddPackageToQuoteMutation,
    useUpdatePackageOnQuoteMutation,
} from "~graphql/generated/graphql"
import { useCheckoutFlow } from "~components/pages/listing/checkout/checkout-flow/checkout-flow-provider"
import type { PackageState } from "~components/pages/listing/checkout/checkout-flow/checkout-flow-types"
import { CHECKOUT_STEPS } from "~config/constants"

import { getAddOnInputsForBackend } from "./quote-helpers"

export default function useAddOrUpdateQuotePackage({
    listingId: nullableListingId,
    quoteId: nullableQuoteId,
    quotePackageId: nullableQuotePackageId,
}: {
    listingId?: string | null
    quoteId?: string | null
    quotePackageId?: string | null
}) {
    const { showError } = useToast()
    const { packageState, goToStep } = useCheckoutFlow()

    const { quoteId, addPackageVariables, updatePackageVariables } =
        formatArgsAndVariables({
            nullableListingId,
            nullableQuoteId,
            nullableQuotePackageId,
            packageState,
        })

    const onCompleted = () => {
        goToStep({
            step: CHECKOUT_STEPS.SUMMARY,
            quote_id: quoteId,
            package_id: null,
            quote_package_id: null,
            checkout_state: null,
        })
    }
    const [addPackageToQuote, { loading: isLoadingAdd }] =
        useAddPackageToQuoteMutation({
            variables: addPackageVariables,
            onCompleted,
            onError: showError,
        })
    const [updatePackageOnQuote, { loading: isLoadingUpdate }] =
        useUpdatePackageOnQuoteMutation({
            variables: updatePackageVariables,
            onCompleted,
            onError: showError,
        })

    const isLoading = isLoadingAdd || isLoadingUpdate

    return { addPackageToQuote, updatePackageOnQuote, isLoading }
}

function formatArgsAndVariables({
    nullableListingId,
    nullableQuoteId,
    nullableQuotePackageId,
    packageState,
}: {
    nullableListingId?: string | null
    nullableQuoteId?: string | null
    nullableQuotePackageId?: string | null
    packageState: PackageState
}) {
    const listingId = nullableListingId ?? ""
    const quoteId = nullableQuoteId ?? ""
    const packageId = nullableQuotePackageId ?? ""
    const pricingPackageId = packageState.pricing_package_id ?? ""

    const addOns = getAddOnInputsForBackend(packageState.add_ons ?? [])
    const sharedDataArgs = {
        number_of_guests: packageState.number_of_guests,
        add_ons: addOns,
    }

    const addPackageVariables = {
        quoteId,
        data: { pricing_package_id: pricingPackageId, ...sharedDataArgs },
    }
    const updatePackageVariables = { quoteId, packageId, data: sharedDataArgs }

    return { listingId, quoteId, addPackageVariables, updatePackageVariables }
}
